(() => {
    const barSelector = '[data-main-product-v2-sticky-bar]';
    const normalizeSellingPlanId = (value) => {
      const normalized = String(value || '').trim();
      return /^\d+$/.test(normalized) && Number(normalized) > 0 ? normalized : '';
    };

    const getJson = (node, selector) => {
      const configNode = node.querySelector(selector);
      if (!configNode) return null;
      try {
        return JSON.parse(configNode.textContent);
      } catch (error) {
        return null;
      }
    };

    const getWidgetPlans = (productInfo) => {
      const plans = [];
      const addPlan = (root, type, key, sellingPlanId) => {
        const id = normalizeSellingPlanId(sellingPlanId);
        if (id) plans.push({ root, type, key, sellingPlanId: id });
      };

      productInfo.querySelectorAll('[data-subscription-widget-v1]').forEach((root) => {
        const config = getJson(root, '[data-subscription-widget-v1-config]');
        addPlan(root, 'v1', 'subscription', config?.options?.subscription?.sellingPlanId);
      });
      productInfo.querySelectorAll('[data-subscription-widget-v2]').forEach((root) => {
        const config = getJson(root, '[data-subscription-widget-v2-config]');
        addPlan(root, 'v2', 'subscription', config?.sellingPlanId);
      });
      productInfo.querySelectorAll('[data-subscription-widget-v3]').forEach((root) => {
        const config = getJson(root, '[data-subscription-widget-v3-config]');
        Object.entries(config?.plans || {}).forEach(([key, plan]) => addPlan(root, 'v3', key, plan?.sellingPlanId));
      });
      productInfo.querySelectorAll('[data-subscription-widget-v4]').forEach((root) => {
        const config = getJson(root, '[data-subscription-widget-v4-config]');
        Object.entries(config?.plans || {}).forEach(([key, plan]) => addPlan(root, 'v4', key, plan?.sellingPlanId));
      });
      return plans;
    };

    const getActiveWidgetSelection = (productInfo, widgetPlans) => {
      for (const widget of productInfo.querySelectorAll('[data-subscription-widget-v4]')) {
        const config = getJson(widget, '[data-subscription-widget-v4-config]');
        const selectedKey = widget.querySelector('[data-subscription-widget-v4-radio]:checked')?.value;
        const selectedId = normalizeSellingPlanId(config?.plans?.[selectedKey]?.sellingPlanId);
        if (selectedId) return { type: 'subscription', sellingPlanId: selectedId };
      }

      for (const widget of productInfo.querySelectorAll('[data-subscription-widget-v3]')) {
        const config = getJson(widget, '[data-subscription-widget-v3-config]');
        const selectedButton = widget.querySelector('[data-subscription-widget-v3-option].subscription-widget-v3__plan--selected, [data-subscription-widget-v3-option][aria-pressed="true"]');
        const selectedKey = selectedButton?.dataset.subscriptionWidgetV3Option;
        if (selectedKey === 'one_time') return { type: 'one_time' };
        const selectedId = normalizeSellingPlanId(config?.plans?.[selectedKey]?.sellingPlanId);
        if (selectedId) return { type: 'subscription', sellingPlanId: selectedId };
      }

      for (const widget of productInfo.querySelectorAll('[data-subscription-widget-v1]')) {
        const config = getJson(widget, '[data-subscription-widget-v1-config]');
        const selectedButton = widget.querySelector('[data-subscription-widget-v1-option][aria-pressed="true"]');
        const selectedKey = selectedButton?.dataset.subscriptionWidgetV1Option;
        if (selectedKey === 'one_time') return { type: 'one_time' };
        const selectedId = normalizeSellingPlanId(config?.options?.subscription?.sellingPlanId);
        if (selectedId) return { type: 'subscription', sellingPlanId: selectedId };
      }

      const firstPlan = widgetPlans[0];
      return firstPlan ? { type: 'subscription', sellingPlanId: firstPlan.sellingPlanId } : { type: 'one_time' };
    };

    const initStickyBar = (bar) => {
      if (!bar || bar.dataset.mainProductV2StickyInitialized === 'true') return;
      const productInfo = bar.closest('product-info');
      const config = getJson(bar, '[data-main-product-v2-sticky-config]');
      const form = bar.querySelector('form');
      const variantInput = bar.querySelector('[data-main-product-v2-sticky-variant-id]');
      const sellingPlanInput = bar.querySelector('[data-main-product-v2-sticky-selling-plan]');
      const addButton = bar.querySelector('[data-main-product-v2-sticky-add-button]');
      const selector = bar.querySelector('[data-main-product-v2-sticky-selector]');
      const selectorTrigger = bar.querySelector('[data-main-product-v2-sticky-selector-trigger]');
      const menu = bar.querySelector('[data-main-product-v2-sticky-menu]');
      const selectedTitle = bar.querySelector('[data-main-product-v2-sticky-selected-title]');
      const selectedTagline = bar.querySelector('[data-main-product-v2-sticky-selected-tagline]');
      const image = bar.querySelector('[data-main-product-v2-sticky-image]');
      const planOptions = [...bar.querySelectorAll('[data-main-product-v2-sticky-plan-option]')];
      if (!productInfo || !config || !form || !variantInput || !sellingPlanInput || !addButton) return;

      bar.dataset.mainProductV2StickyInitialized = 'true';
      const abortController = new AbortController();
      let currentVariant = config.initialVariant || {};
      let visibleOptions = [];
      let selectedOption = null;
      let variantUnsubscriber = null;
      let cartUpdateUnsubscriber = null;
      let cartErrorUnsubscriber = null;
      let stickyLoadingStartedAt = 0;
      let stickyLoadingResetTimer = null;
      let stickyLoadingFailsafeTimer = null;

      const setStickyLoading = (isLoading) => {
        if (isLoading) {
          stickyLoadingStartedAt = Date.now();
          window.clearTimeout(stickyLoadingResetTimer);
          window.clearTimeout(stickyLoadingFailsafeTimer);
          addButton.dataset.stickyLoading = 'true';
          addButton.setAttribute('aria-busy', 'true');
          stickyLoadingFailsafeTimer = window.setTimeout(() => setStickyLoading(false), 3000);
          return;
        }
        const remainingDuration = Math.max(0, 350 - (Date.now() - stickyLoadingStartedAt));
        window.clearTimeout(stickyLoadingResetTimer);
        window.clearTimeout(stickyLoadingFailsafeTimer);
        stickyLoadingResetTimer = window.setTimeout(() => {
          delete addButton.dataset.stickyLoading;
          addButton.removeAttribute('aria-busy');
        }, remainingDuration);
      };

      const setMenuOpen = (open) => {
        if (!selector || !selectorTrigger || !menu || selector.hidden) return;
        selector.dataset.open = open ? 'true' : 'false';
        selectorTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        menu.hidden = !open;
      };

      const updateVariant = (variant) => {
        if (!variant) return;
        currentVariant = { ...currentVariant, ...variant };
        if (variant.id) variantInput.value = variant.id;
        const isAvailable = currentVariant.available !== false && Boolean(variantInput.value);
        variantInput.disabled = !isAvailable;
        addButton.disabled = !isAvailable;
        if (image && variant.featuredImage) image.src = variant.featuredImage;
      };

      const syncWidgetsToSubscription = (sellingPlanId) => {
        getWidgetPlans(productInfo)
          .filter((plan) => plan.sellingPlanId === sellingPlanId)
          .forEach((plan) => {
            if (plan.type === 'v4') {
              const radio = plan.root.querySelector(`[data-subscription-widget-v4-radio][value="${plan.key}"]`);
              if (radio && !radio.checked) {
                radio.checked = true;
                radio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            } else if (plan.type === 'v3') {
              const button = plan.root.querySelector(`[data-subscription-widget-v3-option="${plan.key}"]`);
              if (button) button.click();
            } else if (plan.type === 'v1') {
              const button = plan.root.querySelector('[data-subscription-widget-v1-option="subscription"]');
              if (button) button.click();
            }
          });
      };

      const syncWidgetsToOneTime = () => {
        productInfo.querySelectorAll('[data-subscription-widget-v1-option="one_time"], [data-subscription-widget-v3-option="one_time"]').forEach((button) => button.click());
      };

      const setSelectedOption = (option, syncWidgets = false) => {
        if (!option) return;
        selectedOption = option;
        const isSubscription = option.dataset.planType === 'subscription';
        const sellingPlanId = isSubscription ? normalizeSellingPlanId(option.dataset.sellingPlanId) : '';
        sellingPlanInput.value = sellingPlanId;
        sellingPlanInput.disabled = !sellingPlanId;
        planOptions.forEach((plan) => plan.setAttribute('aria-selected', plan === option ? 'true' : 'false'));
        if (selectedTitle) selectedTitle.textContent = option.dataset.planTitle || '';
        if (selectedTagline) selectedTagline.textContent = option.dataset.planTagline || '';
        if (syncWidgets) {
          if (sellingPlanId) syncWidgetsToSubscription(sellingPlanId);
          else syncWidgetsToOneTime();
        }
      };

      const refreshOptions = (preferWidgetSelection = true) => {
        const widgetPlans = getWidgetPlans(productInfo);
        const validIds = new Set(widgetPlans.map((plan) => plan.sellingPlanId));
        planOptions.forEach((option) => {
          const isOneTime = option.dataset.planType === 'one_time';
          const isValidSubscription = validIds.has(normalizeSellingPlanId(option.dataset.sellingPlanId));
          option.hidden = isOneTime ? !config.showOneTimeOption : !isValidSubscription;
        });
        const subscriptions = planOptions.filter((option) => !option.hidden && option.dataset.planType === 'subscription');
        const oneTimeOption = planOptions.find((option) => !option.hidden && option.dataset.planType === 'one_time');
        visibleOptions = [...subscriptions, ...(oneTimeOption ? [oneTimeOption] : [])];

        if (!subscriptions.length && !oneTimeOption) {
          visibleOptions = [];
          if (selector) selector.hidden = true;
          bar.dataset.stickyHasSelector = 'false';
          sellingPlanInput.value = '';
          sellingPlanInput.disabled = true;
          return;
        }

        if (selector) selector.hidden = visibleOptions.length < 2;
        bar.dataset.stickyHasSelector = visibleOptions.length > 1 ? 'true' : 'false';
        const widgetSelection = preferWidgetSelection ? getActiveWidgetSelection(productInfo, widgetPlans) : null;
        const matchingSelection = widgetSelection?.type === 'subscription'
          ? subscriptions.find((option) => normalizeSellingPlanId(option.dataset.sellingPlanId) === widgetSelection.sellingPlanId)
          : widgetSelection?.type === 'one_time' ? oneTimeOption : null;
        const nextSelection = matchingSelection || (selectedOption && visibleOptions.includes(selectedOption)) || subscriptions[0] || oneTimeOption;
        setSelectedOption(nextSelection, false);
      };

      const updateVisibility = () => {
        const mainProductRect = productInfo.getBoundingClientRect();
        const footer = document.querySelector('footer, [id^="shopify-section-footer"]');
        const footerIsHalfVisible = footer && footer.getBoundingClientRect().top <= window.innerHeight * 0.5;
        const mainProductHasFortyPercentRemaining = mainProductRect.bottom <= window.innerHeight * 0.4;
        bar.dataset.stickyVisible = mainProductHasFortyPercentRemaining && !footerIsHalfVisible ? 'true' : 'false';
      };

      planOptions.forEach((option) => {
        option.addEventListener('click', () => {
          setSelectedOption(option, true);
          setMenuOpen(false);
        }, { signal: abortController.signal });
      });
      selectorTrigger?.addEventListener('click', () => setMenuOpen(menu?.hidden), { signal: abortController.signal });
      selector?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') setMenuOpen(false);
      }, { signal: abortController.signal });
      selector?.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!selector.contains(document.activeElement)) setMenuOpen(false);
        }, 0);
      }, { signal: abortController.signal });
      productInfo.addEventListener('click', (event) => {
        if (!event.target.closest('[data-subscription-widget-v1], [data-subscription-widget-v2], [data-subscription-widget-v3], [data-subscription-widget-v4]')) return;
        window.requestAnimationFrame(() => refreshOptions(true));
      }, { signal: abortController.signal });
      productInfo.addEventListener('change', (event) => {
        if (!event.target.closest('[data-subscription-widget-v1], [data-subscription-widget-v2], [data-subscription-widget-v3], [data-subscription-widget-v4]')) return;
        window.requestAnimationFrame(() => refreshOptions(true));
      }, { signal: abortController.signal });
      addButton.addEventListener('pointerdown', () => {
        if (!addButton.disabled) setStickyLoading(true);
      }, { signal: abortController.signal });
      addButton.addEventListener('click', () => {
        if (!addButton.disabled) setStickyLoading(true);
      }, { signal: abortController.signal });
      form.addEventListener('submit', () => {
        const pageVariantInput = document.getElementById(bar.dataset.productFormId || '')?.querySelector('[name="id"]');
        if (pageVariantInput?.value) updateVariant({ id: pageVariantInput.value, available: !pageVariantInput.disabled });
        setStickyLoading(true);
      }, { signal: abortController.signal });
      const submitStateObserver = new MutationObserver(() => {
        if (!addButton.classList.contains('loading') && addButton.dataset.stickyLoading === 'true') setStickyLoading(false);
      });
      submitStateObserver.observe(addButton, { attributes: true, attributeFilter: ['class'] });
      window.addEventListener('scroll', updateVisibility, { passive: true, signal: abortController.signal });
      window.addEventListener('resize', updateVisibility, { signal: abortController.signal });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || data.sectionId !== config.sectionId || !data.variant) return;
          updateVariant({
            id: data.variant.id,
            available: data.variant.available,
            featuredImage: data.variant.featured_media?.preview_image?.src || data.variant.featured_media?.preview_image?.url
          });
        });
        cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'product-form' && String(event.productVariantId) === String(variantInput.value)) setStickyLoading(false);
        });
        cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event?.source === 'product-form' && String(event.productVariantId) === String(variantInput.value)) setStickyLoading(false);
        });
      }

      refreshOptions(true);
      const initialWidgetSelection = getActiveWidgetSelection(productInfo, getWidgetPlans(productInfo));
      if (
        selectedOption?.dataset.planType === 'subscription'
        && initialWidgetSelection?.sellingPlanId !== normalizeSellingPlanId(selectedOption.dataset.sellingPlanId)
      ) {
        syncWidgetsToSubscription(normalizeSellingPlanId(selectedOption.dataset.sellingPlanId));
      }
      updateVariant(currentVariant);
      updateVisibility();
      bar.__mainProductV2StickyCleanup = () => {
        abortController.abort();
        submitStateObserver.disconnect();
        window.clearTimeout(stickyLoadingResetTimer);
        window.clearTimeout(stickyLoadingFailsafeTimer);
        variantUnsubscriber?.();
        cartUpdateUnsubscriber?.();
        cartErrorUnsubscriber?.();
        delete bar.dataset.mainProductV2StickyInitialized;
      };
    };

    const initBars = (scope = document) => scope.querySelectorAll(barSelector).forEach(initStickyBar);
    const start = () => initBars();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();

    if (!window.__mainProductV2StickyBarEditorEventsBound) {
      window.__mainProductV2StickyBarEditorEventsBound = true;
      document.addEventListener('shopify:section:load', (event) => initBars(event.target));
      document.addEventListener('shopify:section:unload', (event) => {
        event.target.querySelectorAll(barSelector).forEach((bar) => bar.__mainProductV2StickyCleanup?.());
      });
    }
  })();
